import { chunk, first, get, groupBy, omit, omitBy, pick } from 'lodash';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

import { init } from '@/service/generic.service';
import { db } from '@/service/firebase';

import store from '@/store';

const FIRESTORE_BATCH_LIMIT = 500;

const CATALOG_ITEM_TYPES = [
  {
    value: 'fpv',
    label: 'FPVM',
  },
  {
    value: 'permit',
    label: 'Permit',
  },
  {
    value: 'fixture',
    label: 'Fixture',
  },
  {
    value: 'appliance',
    label: 'Appliance',
  },
  {
    value: 'equipment',
    label: 'Equipment',
  },
  {
    value: 'merchandise',
    label: 'Merchandise',
  },
  {
    value: 'labor',
    label: 'Labor',
  },
];

const QUALITY = [
  {
    value: 'good',
    label: 'Good',
  },
  {
    value: 'better',
    label: 'Better',
  },
  {
    value: 'best',
    label: 'Best',
  },
];

const methods = init({
  collectionPath: 'catalogItems',
  algoliaIndex: 'catalogItems',
});

function forPersist(record) {
  return omit(record, ['supplier', 'supplierItem', 'supplierItems', 'supplierLine']);
}

export default {
  list: methods.list,
  getById,
  create: record => withCacheReset(() => methods.create(forPersist(record))),
  update: (id, record, merge) => withCacheReset(() => methods.update(id, forPersist(record), merge)),
  getItemsForProduct,
  setSupplier,
  clearSupplier,
  editAssignedSupplier,
  setActive,
  bulkSetActive,
  softDelete,
  bulkSoftDelete,
  CATALOG_ITEM_TYPES,
  QUALITY,
};

function buildAuditFields() {
  const user = store.getters['auth/user']?.employee;
  return {
    updatedAt: new Date(),
    updatedBy: user ? pick(user, ['firstName', 'lastName', 'id', 'workflowFunction', 'code']) : undefined,
  };
}

async function commitInChunks(ids, buildData, onProgress) {
  const collection = db.collection('catalogItems');
  const chunks = chunk(ids, FIRESTORE_BATCH_LIMIT);
  const total = chunks.length;
  let completed = 0;
  if (onProgress) onProgress({ completed, total });
  await Promise.all(
    chunks.map(idsChunk => {
      const batch = db.batch();
      idsChunk.forEach(id => batch.set(collection.doc(id), buildData(), { merge: true }));
      return batch.commit().then(() => {
        completed += 1;
        if (onProgress) onProgress({ completed, total });
      });
    })
  );
  return ids;
}

async function withCacheReset(run) {
  const result = await run();
  await methods.clearSearchCache();
  return result;
}

async function setActive(id, isActive) {
  return withCacheReset(() => methods.update(id, { isActive }, true));
}

async function bulkSetActive(ids, isActive, onProgress) {
  return withCacheReset(() => commitInChunks(ids, () => ({ isActive, ...buildAuditFields() }), onProgress));
}

async function softDelete(id) {
  return withCacheReset(() => commitInChunks([id], () => ({ deleted: true, ...buildAuditFields() })));
}

async function bulkSoftDelete(ids, onProgress) {
  return withCacheReset(() => commitInChunks(ids, () => ({ deleted: true, ...buildAuditFields() }), onProgress));
}

async function setSupplier(catalogItemId, newSupplier) {
  const catalogSnap = await db.collection('catalogItems').doc(catalogItemId).get();
  if (!catalogSnap.exists) throw new Error('Catalog item not found');
  const supplier = omitBy(
    {
      ...newSupplier,
      pricedAt: firebase.firestore.FieldValue.serverTimestamp(),
    },
    v => v === undefined
  );
  return methods.update(catalogItemId, { supplier }, true);
}

async function editAssignedSupplier(catalogItemId, supplierRowId, newSupplier) {
  const snap = await db.collection('catalogItems').doc(catalogItemId).get();
  if (!snap.exists) throw new Error('Catalog item not found');
  const cur = snap.data().supplier;
  if (cur && typeof cur === 'object' && cur.id && !cur.supplierItemId && cur.id === supplierRowId) {
    return methods.update(
      catalogItemId,
      {
        supplier: {
          ...cur,
          ...newSupplier,
          pricedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
      },
      true
    );
  }
  throw new Error('Supplier not found');
}

async function clearSupplier(catalogItemId) {
  return methods.update(catalogItemId, { supplier: null }, true);
}

async function getById(id) {
  const doc = await db.collection('catalogItems').doc(id).get();
  const data = { ...doc.data(), id: doc.id };
  return { ...data, isActive: data.isActive ?? true };
}

async function getItemsForProduct(product) {
  if (!product.items) return [];
  const items = product.items.map(i => i.itemId).filter(i => i);

  const itemsById = groupBy(await methods.getChunkedDBResults(items), 'id');
  return product.items.map(i => ({ ...i, ...first(get(itemsById, i.itemId)) }));
}
